import { useState } from 'react';
import { createPortal } from 'react-dom';

import {
  FONT_CALIBRE_SCHOOL,
  FONT_GOOGLE_MONTSERRAT,
  FONT_GOOGLE_NSW_HANDWRITING,
  FONT_GOOGLE_ROBOTO,
  FONT_TIMES_NEW_ROMAN,
} from 'approot/shared/styles/typography.style';
import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import { ToolScale, SUB_MENU_PORTAL_ID } from '../draw-canvas.constants';
import ToolSize from './tool-size';
import { ToolBarTool } from './toolbar.style';
import ToolbarItem from './toolbar-item';
import { DropdownItem } from 'approot/shared/dropdown/dropdown-item.style';
import IconFontType from 'approot/shared/inline-svg/activity-builder/icon-font-type';
import * as Styled from './tool-text-size.style';

type Props = {
  size: number;
  font: string;
  scale: ToolScale;
  onSelect: (size: number) => void;
  onFontSelect: (font: string) => void;
};

const ToolTextStyle = ({ size, font, onSelect, onFontSelect }: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const { styles, attributes, setReferenceElement, setPopperElement } =
    useContextualSurface(isActive, setIsActive, {
      offset: [0, 12],
      placement: 'left-start',
      strategy: 'fixed',
    });

  const portalContainer = document.getElementById(SUB_MENU_PORTAL_ID);

  const renderTextStylePanel = () => (
    <div
      ref={setPopperElement}
      role="dialog"
      tabIndex={-1}
      style={styles.popper}
      {...attributes.popper}
    >
      <Styled.ToolTextOptionsContainer>
        <ToolSize
          size={size}
          scale={{
            min: 10,
            max: 50,
            step: 1,
          }}
          hidePreview
          setSize={onSelect}
        />
        <Styled.ToolTextOptions $flexDirection="column">
          <DropdownItem
            type="button"
            $isActive={font.includes(FONT_CALIBRE_SCHOOL)}
            onClick={() => onFontSelect(FONT_CALIBRE_SCHOOL)}
          >
            Simple
          </DropdownItem>
          <DropdownItem
            type="button"
            $isActive={font === FONT_GOOGLE_MONTSERRAT}
            style={{ fontFamily: FONT_GOOGLE_MONTSERRAT }}
            onClick={() => onFontSelect(FONT_GOOGLE_MONTSERRAT)}
          >
            Modern
          </DropdownItem>
          <DropdownItem
            type="button"
            $isActive={font === FONT_GOOGLE_ROBOTO}
            style={{ fontFamily: FONT_GOOGLE_ROBOTO }}
            onClick={() => onFontSelect(FONT_GOOGLE_ROBOTO)}
          >
            Technical
          </DropdownItem>
          <DropdownItem
            type="button"
            $isActive={font === FONT_TIMES_NEW_ROMAN}
            style={{ fontFamily: FONT_TIMES_NEW_ROMAN }}
            onClick={() => onFontSelect(FONT_TIMES_NEW_ROMAN)}
          >
            Classic
          </DropdownItem>

          <DropdownItem
            type="button"
            $isActive={font === FONT_GOOGLE_NSW_HANDWRITING}
            style={{ fontFamily: FONT_GOOGLE_NSW_HANDWRITING }}
            onClick={() => onFontSelect(FONT_GOOGLE_NSW_HANDWRITING)}
          >
            Handwriting
          </DropdownItem>
        </Styled.ToolTextOptions>
      </Styled.ToolTextOptionsContainer>
    </div>
  );

  return (
    <>
      <ToolbarItem hoverText="Text Style">
        <ToolBarTool
          ref={setReferenceElement}
          $isActive={isActive}
          aria-label="Change the style of size and font of text"
          onClick={() => setIsActive(!isActive)}
        >
          <IconFontType width={20} height={20} />
        </ToolBarTool>
      </ToolbarItem>
      {isActive &&
        (portalContainer
          ? createPortal(renderTextStylePanel(), portalContainer)
          : renderTextStylePanel())}
    </>
  );
};

export default ToolTextStyle;
